package com.daytimegaming.ourdailyfinances.presentation.account

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.isSystemInDarkTheme
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.wrapContentWidth
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.Edit
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.material3.TopAppBar
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.tooling.preview.PreviewLightDark
import androidx.compose.ui.tooling.preview.PreviewParameter
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import com.daytimegaming.ourdailyfinances.presentation.components.AccountBalanceOverTimeCard
import com.daytimegaming.ourdailyfinances.presentation.preview.AccountDetailPreviewParameterProvider
import com.daytimegaming.ourdailyfinances.presentation.theme.AppTheme
import com.daytimegaming.ourdailyfinances.presentation.theme.GlassCard
import com.daytimegaming.ourdailyfinances.presentation.util.formatAmount
import com.daytimegaming.ourdailyfinances.presentation.util.formatIsoDateTime
import com.daytimegaming.ourdailyfinances.presentation.util.toCurrencySymbol
import org.koin.compose.viewmodel.koinViewModel
import org.koin.core.parameter.parametersOf


@Composable
fun AccountDetailScreen(
    accountId: String,
    dashboardId: String? = null,
    onNavigateBack: () -> Unit,
    onTransactionClick: (String) -> Unit,
    onHoldingClick: (String) -> Unit,
    onInvalidAccess: (() -> Unit)? = null,
    viewModel: AccountDetailViewModel = koinViewModel(key = "$accountId-$dashboardId") { parametersOf(accountId, dashboardId) }
) {
    val state by viewModel.state.collectAsStateWithLifecycle()

    LaunchedEffect(state) {
        if (state is AccountDetailScreenState.Error && onInvalidAccess != null) {
            onInvalidAccess()
        }
    }

    AccountDetailScreenContent(
        state = state,
        onNavigateBack = onNavigateBack,
        onTransactionClick = onTransactionClick,
        onHoldingClick = onHoldingClick,
        onUpdateNickname = { nickname, onSuccess, onError ->
            viewModel.updateNickname(nickname, onSuccess, onError)
        }
    )
}

@Composable
fun AccountDetailScreenContent(
    state: AccountDetailScreenState,
    onNavigateBack: () -> Unit,
    onTransactionClick: (String) -> Unit,
    onHoldingClick: (String) -> Unit = {},
    onUpdateNickname: (String?, onSuccess: () -> Unit, onError: (String) -> Unit) -> Unit = { _, onSuccess, _ -> onSuccess() }
) {
    val listState = rememberLazyListState()
    var showEditDialog by remember { mutableStateOf(false) }
    Scaffold(
        topBar = {
            TopAppBar(
                title = { Text(text = "Account Details") },
                navigationIcon = {
                    IconButton(onClick = onNavigateBack) {
                        Icon(
                            imageVector = Icons.AutoMirrored.Filled.ArrowBack,
                            contentDescription = "Back"
                        )
                    }
                }
            )
        }
    ) { innerPadding ->
        Box(
            modifier = Modifier
                .fillMaxSize()
                .padding(innerPadding)
        ) {
            when (state) {
                is AccountDetailScreenState.Loading -> {
                    Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                        CircularProgressIndicator()
                    }
                }
                is AccountDetailScreenState.Error -> {
                    Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                        Text(text = state.message, color = MaterialTheme.colorScheme.error)
                    }
                }
                is AccountDetailScreenState.Loaded -> {
                    val account = state.account
                    val currencySymbol = account.isoCurrencyCode.toCurrencySymbol()

                    if (showEditDialog) {
                        var nicknameText by remember { mutableStateOf(account.nickname ?: "") }
                        var dialogError by remember { mutableStateOf<String?>(null) }
                        var isUpdating by remember { mutableStateOf(false) }

                        AlertDialog(
                            onDismissRequest = { if (!isUpdating) showEditDialog = false },
                            title = { Text(text = "Edit Account Nickname") },
                            text = {
                                Column {
                                    Text(
                                        text = account.name,
                                        style = MaterialTheme.typography.titleMedium,
                                        color = MaterialTheme.colorScheme.onSurfaceVariant
                                    )
                                    Spacer(modifier = Modifier.height(12.dp))
                                    OutlinedTextField(
                                        value = nicknameText,
                                        onValueChange = { nicknameText = it },
                                        label = { Text("Nickname") },
                                        singleLine = true,
                                        modifier = Modifier.fillMaxWidth(),
                                        enabled = !isUpdating
                                    )
                                    if (dialogError != null) {
                                        Spacer(modifier = Modifier.height(8.dp))
                                        Text(
                                            text = dialogError!!,
                                            color = MaterialTheme.colorScheme.error,
                                            style = MaterialTheme.typography.bodySmall
                                        )
                                    }
                                }
                            },
                            confirmButton = {
                                TextButton(
                                    enabled = !isUpdating,
                                    onClick = {
                                        isUpdating = true
                                        dialogError = null
                                        val trimmed = nicknameText.trim()
                                        onUpdateNickname(trimmed, {
                                            isUpdating = false
                                            showEditDialog = false
                                        }, { error ->
                                            isUpdating = false
                                            dialogError = error
                                        })
                                    }
                                ) {
                                    Text("Update")
                                }
                            },
                            dismissButton = {
                                Row(
                                    horizontalArrangement = Arrangement.End,
                                    verticalAlignment = Alignment.CenterVertically
                                ) {
                                    TextButton(
                                        enabled = !isUpdating,
                                        onClick = { showEditDialog = false }
                                    ) {
                                        Text("Cancel")
                                    }
                                    if (!account.nickname.isNullOrEmpty()) {
                                        Spacer(modifier = Modifier.width(8.dp))
                                        TextButton(
                                            enabled = !isUpdating,
                                            onClick = {
                                                isUpdating = true
                                                dialogError = null
                                                onUpdateNickname("", {
                                                    isUpdating = false
                                                    showEditDialog = false
                                                }, { error ->
                                                    isUpdating = false
                                                    dialogError = error
                                                })
                                            }
                                        ) {
                                            Text(
                                                text = "Remove",
                                                color = MaterialTheme.colorScheme.error
                                            )
                                        }
                                    }
                                }
                            }
                        )
                    }
                    val rawBalance = account.currentBalance ?: 0.0
                    val isInvestment = account.type.lowercase() == "investment"
                    val totalHoldingsValue = if (isInvestment) state.holdings.sumOf { it.institutionValue ?: (it.quantity * it.institutionPrice) } else 0.0
                    val currentBalance = if (account.type.lowercase() == "credit" || account.type.lowercase() == "loan") {
                        -rawBalance
                    } else if (isInvestment && rawBalance <= 0.0 && totalHoldingsValue > 0.0) {
                        totalHoldingsValue
                    } else {
                        rawBalance
                    }

                    LazyColumn(
                        state = listState,
                        modifier = Modifier.fillMaxSize(),
                        verticalArrangement = Arrangement.spacedBy(12.dp),
                        contentPadding = PaddingValues(bottom = 24.dp),
                    ) {
                        item {
                            // Account Hero Card
                            GlassCard(modifier = Modifier.fillMaxWidth().padding(start = 16.dp, end = 16.dp, top = 16.dp, bottom = 4.dp)) {
                                account.institutionName?.let {
                                    Text(
                                        text = it,
                                        style = MaterialTheme.typography.labelSmall,
                                        color = MaterialTheme.colorScheme.onSurfaceVariant
                                    )
                                    Spacer(modifier = Modifier.height(4.dp))
                                }
                                Row(
                                    modifier = Modifier.fillMaxWidth(),
                                    verticalAlignment = Alignment.CenterVertically,
                                    horizontalArrangement = Arrangement.SpaceBetween
                                ) {
                                    Column(modifier = Modifier.weight(1f)) {
                                        Text(
                                            text = account.displayName,
                                            style = MaterialTheme.typography.headlineLarge,
                                            color = MaterialTheme.colorScheme.onSurface
                                        )
                                        Text(
                                            text = account.subtype?.uppercase() ?: account.type.uppercase(),
                                            style = MaterialTheme.typography.bodySmall,
                                            color = MaterialTheme.colorScheme.onSurfaceVariant
                                        )
                                    }
                                    if (state.isOwner) {
                                        IconButton(
                                            onClick = { showEditDialog = true }
                                        ) {
                                            Icon(
                                                imageVector = Icons.Default.Edit,
                                                contentDescription = "Edit Account Nickname",
                                                tint = MaterialTheme.colorScheme.primary
                                            )
                                        }
                                    }
                                }

                                Spacer(modifier = Modifier.height(16.dp))

                                val isNegative = currentBalance < 0
                                val absBalance = kotlin.math.abs(currentBalance)
                                val formattedBalance = absBalance.formatAmount()
                                val parts = formattedBalance.split('.')
                                val intPart = parts.getOrElse(0) { "0" }
                                val decPart = parts.getOrNull(1) ?: "00"

                                Row(
                                    verticalAlignment = Alignment.Bottom
                                ) {
                                    if (isNegative) {
                                        Text(
                                            text = "-",
                                            style = MaterialTheme.typography.titleLarge,
                                            color = MaterialTheme.colorScheme.error,
                                            modifier = Modifier.padding(bottom = 12.dp)
                                        )
                                    }
                                    Text(
                                        text = currencySymbol,
                                        style = MaterialTheme.typography.titleLarge,
                                        color = MaterialTheme.colorScheme.primary,
                                        modifier = Modifier.padding(bottom = 12.dp)
                                    )
                                    Text(
                                        text = intPart,
                                        style = MaterialTheme.typography.displayLarge.copy(fontSize = 48.sp),
                                        color = MaterialTheme.colorScheme.onSurface,
                                        fontWeight = FontWeight.Bold
                                    )
                                    Text(
                                        text = ".$decPart",
                                        style = MaterialTheme.typography.titleLarge,
                                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                                        modifier = Modifier.padding(bottom = 8.dp)
                                    )
                                }
                                account.availableBalance?.let { available ->
                                    Spacer(modifier = Modifier.height(8.dp))
                                    Text(
                                        text = "Available Balance: $currencySymbol ${available.formatAmount()}",
                                        style = MaterialTheme.typography.labelMedium,
                                        color = MaterialTheme.colorScheme.secondary
                                    )
                                }
                                state.plaidItem?.let { item ->
                                    val balancesSynced = item.balancesLastSyncedAt?.let { formatIsoDateTime(it) } ?: "Never"
                                    Spacer(modifier = Modifier.height(8.dp))
                                    Text(
                                        text = "Last Synced: $balancesSynced",
                                        style = MaterialTheme.typography.labelSmall,
                                        color = MaterialTheme.colorScheme.secondary
                                    )
                                }
                            }
                        }

                        item {
                            Box(modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp)) {
                                AccountBalanceOverTimeCard(
                                    currentBalance = currentBalance,
                                    transactions = state.transactions,
                                    holdings = state.holdings,
                                    securityPerformance = state.securityPerformance,
                                    isInvestment = account.type.lowercase() == "investment",
                                    isSecurityPerformanceLoading = state.isSecurityPerformanceLoading,
                                    currencySymbol = currencySymbol,
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .padding(bottom = 16.dp)
                                )
                            }
                        }

                        if (account.type.lowercase() == "investment") {
                            // Section 1: Holdings
                            item {
                                Text(
                                    modifier = Modifier.padding(horizontal = 16.dp, vertical = 4.dp),
                                    text = "Portfolio Holdings",
                                    style = MaterialTheme.typography.headlineMedium,
                                    color = MaterialTheme.colorScheme.onSurface
                                )
                            }

                            if (state.holdings.isEmpty()) {
                                item {
                                    GlassCard(modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp)) {
                                        Text(
                                            text = "No holdings found.",
                                            style = MaterialTheme.typography.bodyMedium,
                                            color = MaterialTheme.colorScheme.onSurfaceVariant
                                        )
                                    }
                                }
                            } else {
                                items(state.holdings) { holding ->
                                    Box(modifier = Modifier.padding(horizontal = 16.dp)) {
                                        GlassCard(
                                            modifier = Modifier
                                                .fillMaxWidth()
                                                .clickable { onHoldingClick(holding.securityId) }
                                        ) {
                                            Row(
                                                modifier = Modifier.fillMaxWidth(),
                                                horizontalArrangement = Arrangement.SpaceBetween,
                                                verticalAlignment = Alignment.CenterVertically
                                            ) {
                                                Column(
                                                    modifier = Modifier.weight(1.3f)
                                                ) {
                                                    Text(
                                                        text = holding.security?.name ?: holding.security?.tickerSymbol ?: "Unknown Security",
                                                        style = MaterialTheme.typography.headlineMedium,
                                                        color = MaterialTheme.colorScheme.onSurface
                                                    )
                                                    Spacer(modifier = Modifier.height(2.dp))
                                                    Row(
                                                        verticalAlignment = Alignment.CenterVertically,
                                                        horizontalArrangement = Arrangement.spacedBy(8.dp)
                                                    ) {
                                                        holding.security?.tickerSymbol?.let { ticker ->
                                                            Box(
                                                                modifier = Modifier
                                                                    .background(Color(0x1FBEC6E0), shape = RoundedCornerShape(999.dp))
                                                                    .padding(horizontal = 8.dp, vertical = 2.dp)
                                                            ) {
                                                                Text(
                                                                    text = ticker,
                                                                    style = MaterialTheme.typography.labelSmall,
                                                                    color = MaterialTheme.colorScheme.secondary
                                                                )
                                                            }
                                                        }
                                                        Text(
                                                            text = "${holding.quantity.formatAmount()} shares",
                                                            style = MaterialTheme.typography.bodyMedium,
                                                            color = MaterialTheme.colorScheme.onSurfaceVariant
                                                        )
                                                        holding.costBasis?.let { cost ->
                                                            Text(
                                                                text = "• Cost: $currencySymbol${cost.formatAmount()}",
                                                                style = MaterialTheme.typography.bodyMedium,
                                                                color = MaterialTheme.colorScheme.onSurfaceVariant
                                                            )
                                                        }
                                                    }
                                                }

                                                Spacer(modifier = Modifier.width(8.dp))

                                                val totalValue = holding.institutionValue ?: (holding.institutionPrice * holding.quantity)
                                                Column(
                                                    horizontalAlignment = Alignment.End
                                                ) {
                                                    Text(
                                                        modifier = Modifier.wrapContentWidth(),
                                                        text = "$currencySymbol${totalValue.formatAmount()}",
                                                        style = MaterialTheme.typography.headlineMedium,
                                                        color = MaterialTheme.colorScheme.onSurface,
                                                        textAlign = TextAlign.End,
                                                        fontWeight = FontWeight.Bold
                                                    )
                                                    Text(
                                                        modifier = Modifier.wrapContentWidth(),
                                                        text = "@ $currencySymbol${holding.institutionPrice.formatAmount()}",
                                                        style = MaterialTheme.typography.labelSmall,
                                                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                                                        textAlign = TextAlign.End
                                                    )
                                                }
                                            }
                                        }
                                    }
                                }
                            }

                            // Section 2: Investment Transactions
                            item {
                                Spacer(modifier = Modifier.height(12.dp))
                                Text(
                                    modifier = Modifier.padding(horizontal = 16.dp, vertical = 4.dp),
                                    text = "Investment Transactions",
                                    style = MaterialTheme.typography.headlineMedium,
                                    color = MaterialTheme.colorScheme.onSurface
                                )
                            }

                            if (state.investmentTransactions.isEmpty()) {
                                item {
                                    GlassCard(modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp)) {
                                        Text(
                                            text = "No recent investment transactions found.",
                                            style = MaterialTheme.typography.bodyMedium,
                                            color = MaterialTheme.colorScheme.onSurfaceVariant
                                        )
                                    }
                                }
                            } else {
                                items(state.investmentTransactions) { tx ->
                                    Box(modifier = Modifier.padding(horizontal = 16.dp)) {
                                        GlassCard(
                                            modifier = Modifier.fillMaxWidth()
                                        ) {
                                            Row(
                                                modifier = Modifier.fillMaxWidth(),
                                                horizontalArrangement = Arrangement.SpaceBetween,
                                                verticalAlignment = Alignment.CenterVertically
                                            ) {
                                                Column(
                                                    modifier = Modifier.weight(1.3f)
                                                ) {
                                                    Text(
                                                        text = tx.name,
                                                        style = MaterialTheme.typography.headlineMedium,
                                                        color = MaterialTheme.colorScheme.onSurface
                                                    )
                                                    Spacer(modifier = Modifier.height(2.dp))
                                                    Row(
                                                        verticalAlignment = Alignment.CenterVertically,
                                                        horizontalArrangement = Arrangement.spacedBy(8.dp)
                                                    ) {
                                                        Text(
                                                            text = tx.date,
                                                            style = MaterialTheme.typography.labelSmall,
                                                            color = MaterialTheme.colorScheme.onSurfaceVariant
                                                        )
                                                        Box(
                                                            modifier = Modifier
                                                                .background(Color(0x1FBEC6E0), shape = RoundedCornerShape(999.dp))
                                                                .padding(horizontal = 8.dp, vertical = 2.dp)
                                                        ) {
                                                            Text(
                                                                text = tx.type.uppercase(),
                                                                style = MaterialTheme.typography.labelSmall,
                                                                color = MaterialTheme.colorScheme.secondary
                                                            )
                                                        }
                                                        if (tx.quantity != null && tx.price != null) {
                                                            Text(
                                                                text = "${tx.quantity.formatAmount()} @ $currencySymbol${tx.price.formatAmount()}",
                                                                style = MaterialTheme.typography.bodyMedium,
                                                                color = MaterialTheme.colorScheme.onSurfaceVariant
                                                            )
                                                        }
                                                    }
                                                }

                                                Spacer(modifier = Modifier.width(8.dp))

                                                val formattedAmount = if (tx.type == "buy") {
                                                    "-$currencySymbol${tx.amount.formatAmount()}"
                                                } else if (tx.type == "sell") {
                                                    "+$currencySymbol${tx.amount.formatAmount()}"
                                                } else {
                                                    if (tx.amount > 0) {
                                                        "-$currencySymbol${tx.amount.formatAmount()}"
                                                    } else {
                                                        "+$currencySymbol${kotlin.math.abs(tx.amount).formatAmount()}"
                                                    }
                                                }
                                                val color = if (tx.type == "sell" || (tx.type != "buy" && tx.amount < 0)) {
                                                    MaterialTheme.colorScheme.primary
                                                } else {
                                                    MaterialTheme.colorScheme.onSurface
                                                }
                                                Text(
                                                    modifier = Modifier.wrapContentWidth(),
                                                    text = formattedAmount,
                                                    style = MaterialTheme.typography.headlineMedium,
                                                    color = color,
                                                    textAlign = TextAlign.End,
                                                )
                                            }
                                        }
                                    }
                                }
                            }
                        } else {
                            // Section: Cash / Depository Transactions (existing code)
                            item {
                                Text(
                                    modifier = Modifier.padding(horizontal = 16.dp, vertical = 4.dp),
                                    text = "Transactions",
                                    style = MaterialTheme.typography.headlineMedium,
                                    color = MaterialTheme.colorScheme.onSurface
                                )
                            }

                            if (state.transactions.isEmpty()) {
                                item {
                                    GlassCard(modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp)) {
                                        Text(
                                            text = "No recent transactions found.",
                                            style = MaterialTheme.typography.bodyMedium,
                                            color = MaterialTheme.colorScheme.onSurfaceVariant
                                        )
                                    }
                                }
                            } else {
                                items(state.transactions) { tx ->
                                    Box(modifier = Modifier.padding(horizontal = 16.dp)) {
                                        GlassCard(
                                            modifier = Modifier
                                                .fillMaxWidth()
                                                .clickable { onTransactionClick(tx.transactionId) }
                                        ) {
                                            Row(
                                                modifier = Modifier.fillMaxWidth(),
                                                horizontalArrangement = Arrangement.SpaceBetween,
                                                verticalAlignment = Alignment.CenterVertically
                                            ) {
                                                Column(
                                                    modifier = Modifier.weight(1.3f)
                                                ) {
                                                    Text(
                                                        text = tx.merchantName ?: tx.name,
                                                        style = MaterialTheme.typography.headlineMedium,
                                                        color = MaterialTheme.colorScheme.onSurface
                                                    )
                                                     val details = listOfNotNull(account.displayName, account.institutionName).joinToString(" • ")
                                                    if (details.isNotEmpty()) {
                                                        Spacer(modifier = Modifier.height(2.dp))
                                                        Text(
                                                            text = details,
                                                            style = MaterialTheme.typography.bodyMedium,
                                                            color = MaterialTheme.colorScheme.onSurfaceVariant
                                                        )
                                                    }
                                                    Spacer(modifier = Modifier.height(4.dp))
                                                    Row(
                                                        verticalAlignment = Alignment.CenterVertically,
                                                        horizontalArrangement = Arrangement.spacedBy(8.dp)
                                                    ) {
                                                        Text(
                                                            text = tx.date,
                                                            style = MaterialTheme.typography.labelSmall,
                                                            color = MaterialTheme.colorScheme.onSurfaceVariant
                                                        )
                                                        Box(
                                                            modifier = Modifier
                                                                .background(Color(0x1FBEC6E0), shape = RoundedCornerShape(999.dp))
                                                                .padding(horizontal = 8.dp, vertical = 2.dp)
                                                        ) {
                                                            Text(
                                                                text = tx.category.firstOrNull()?.replace("_", " ") ?: "",
                                                                style = MaterialTheme.typography.labelSmall,
                                                                color = MaterialTheme.colorScheme.secondary
                                                            )
                                                        }
                                                        if (tx.pending) {
                                                            Box(
                                                                modifier = Modifier
                                                                    .background(Color(0x33FFB4AB), shape = RoundedCornerShape(999.dp))
                                                                    .padding(horizontal = 8.dp, vertical = 2.dp)
                                                            ) {
                                                                Text(
                                                                    text = "Pending",
                                                                    style = MaterialTheme.typography.labelSmall,
                                                                    color = MaterialTheme.colorScheme.error
                                                                )
                                                            }
                                                        }
                                                    }
                                                }

                                                Spacer(modifier = Modifier.width(8.dp))

                                                // Plaid API: positive = debit (money out), negative = credit (money in)
                                                val formattedAmount = if (tx.amount > 0) {
                                                    "-$currencySymbol${tx.amount.formatAmount()}"
                                                } else {
                                                    "+$currencySymbol${kotlin.math.abs(tx.amount).formatAmount()}"
                                                }
                                                val color = if (tx.amount < 0) MaterialTheme.colorScheme.primary else MaterialTheme.colorScheme.onSurface
                                                Text(
                                                    modifier = Modifier.wrapContentWidth(),
                                                    text = formattedAmount,
                                                    style = MaterialTheme.typography.headlineMedium,
                                                    color = color,
                                                    textAlign = TextAlign.End,
                                                )
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

@PreviewLightDark
@Composable
fun AccountDetailScreenPreview(
    @PreviewParameter(AccountDetailPreviewParameterProvider::class) state: AccountDetailScreenState
) {
    AppTheme(
        darkTheme = isSystemInDarkTheme(),
        dynamicColor = false,
    ) {
        Surface {
            AccountDetailScreenContent(
                state = state,
                onNavigateBack = {},
                onTransactionClick = {}
            )
        }
    }
}
